<template>
  <div class="hyp-container">
    <v-container fluid bg fill-height grid-list-md text-center>
      <v-row row wrap align="center">
        <v-col>
          <Loader text="Loading Seerist" color="white" size="x-large"></Loader>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import router from "@/router";
import {
  getIdToken,
  getAccessToken,
  setIdToken,
  setAccessToken,
  getAndSetUserProfile,
  setUserGroups,
  getUserGroups,
} from "@/common/storage";
import {
  pageTrackerLogger,
  hasInternalToken,
  retrieveUserGroups,
} from "@/common/pageTracker";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import {
  getPreferredLandingPage,
  isAdmin,
  isInIgnoreUserList,
  isInternalUser,
  datadogSetUserMetadata,
} from "@/services/user/user.service";
import { authReady, getAuthErrorByUrl } from "@/common/authenticate";
import { ASSETS_ROUTE } from "@/components/assetManagement/AssetsSelector.vue";

export default {
  name: "Callback",
  components: {
    Loader: () => import("@/components/common/Loader"),
  },
  async mounted() {
    await authReady();
    this.$nextTick(async () => {
      const message = getAuthErrorByUrl();
      if (message) {
        router.push({ name: "UrlError", params: { message } });
        return null;
      }

      if (this.isInvalidLoginTicket()) {
        router.push({ name: "InvalidLoginTicket" });
        return null;
      }

      if (this.isEmailNotVerified()) {
        router.push({ name: "EmailVerification" });
        return null;
      }

      if (this.isTrialExpired()) {
        router.push({ name: "TrialExpired" });
        return null;
      }

      if (this.isRegistrationTokenMissing()) {
        router.push({ name: "RegistrationTokenMissing" });
        return null;
      }

      let accessToken = await this.$auth.getTokenSilently();
      setAccessToken(accessToken);
      if (!getAccessToken()) {
        router.push({ name: "401" });
        return null;
      }

      let idTokenClaims = await this.$auth.getIdTokenClaims();
      setIdToken(idTokenClaims.__raw);
      if (!getIdToken()) {
        router.push({ name: "401" });
        return null;
      }

      const RESULT = await getAndSetUserProfile();

      if (!RESULT) {
        router.push({ path: "unknown" });
      } else if (RESULT === 401) {
        router.push({ name: "401" });
      } else {
        await setUserGroups();
        getUserGroups();

        //TODO: This is probably overkill but we want this...
        this.pageTrackerSignin();
        datadogSetUserMetadata();

        if (this.$route.query.hasOwnProperty("redirect")) {
          // grab extra params not in .redirect
          const params = JSON.parse(JSON.stringify(this.$route.query));
          delete params.redirect;

          // copy over params that were cut off from .redirect param
          for (let param in params) {
            this.$route.query.redirect +=
              this.$route.query.redirect.indexOf("?") > -1
                ? `&${param}=${params[param]}`
                : `?${param}=${params[param]}`;
          }

          if (
            this.$route.query.redirect.indexOf("/watch") > -1 ||
            this.$route.query.redirect.indexOf("/savedsearch") > -1 ||
            this.$route.query.redirect.indexOf("/search") > -1 ||
            this.$route.query.redirect.indexOf("/content") > -1 ||
            this.$route.query.redirect.indexOf(ASSETS_ROUTE) > -1
          ) {
            router.push({ path: this.$route.query.redirect });
            return;
          }
        }

        //Route to the user's preferred landing page
        router.push({ name: getPreferredLandingPage() });
      }
    });
  },
  methods: {
    isInvalidLoginTicket() {
      const AUTH_ERROR = this.getAuthError();
      if (
        AUTH_ERROR &&
        (AUTH_ERROR === "Unknown or invalid login ticket." ||
          AUTH_ERROR === "Unable to configure verification page.")
      ) {
        return true;
      }
      return false;
    },
    isEmailNotVerified() {
      const AUTH_ERROR = this.getAuthError();
      if (AUTH_ERROR && AUTH_ERROR === "EMAIL_NOT_VERIFIED") {
        return true;
      }
      return false;
    },
    isTrialExpired() {
      const AUTH_ERROR = this.getAuthError();
      if (AUTH_ERROR && AUTH_ERROR === "TRIAL_EXPIRED") {
        return true;
      }
      return false;
    },
    isRegistrationTokenMissing() {
      const AUTH_ERROR = this.getAuthError();
      if (AUTH_ERROR && AUTH_ERROR === "REGISTRATION_TOKEN_MISSING") {
        return true;
      }
      return false;
    },
    getAuthError() {
      let match = RegExp("[#&]" + "error_description" + "=([^&]*)").exec(
        window.location.hash
      );
      const ERROR = match && decodeURIComponent(match[1].replace(/\+/g, " "));
      return ERROR;
    },
    pageTrackerSignin() {
      pageTrackerLogger("Auth0 Callback -> pageTrackerSignin....");
      let _paq = (window._paq = window._paq || []);
      const profile = JSON.parse(localStorage.getItem("user_profile"));
      if (profile && profile.email) {
        _paq.push(["setUserId", profile.email]);
        pageTrackerLogger("Matomo will store the userId " + profile.email);
        // Always store some login event (default text when not found)
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.system,
          EVENT_ACTIONS.loggedIn,
          profile.email
        );
      }

      if (
        profile &&
        profile.user_metadata &&
        profile.user_metadata.signup_organization
      ) {
        _paq.push([
          "setCustomVariable",
          1,
          "Customer",
          profile.user_metadata.signup_organization,
          "visit",
        ]);
      } else {
        pageTrackerLogger(
          "The user profile signup_organization was not found!"
        );
      }

      const userGroups =
        profile && profile.userGroups
          ? retrieveUserGroups(profile.userGroups)
          : null;
      if (userGroups) {
        const filteredUserGroups = userGroups.filter((group) => {
          return group.toLowerCase().indexOf("user_") === 0;
        });
        const group = filteredUserGroups[0];
        _paq.push(["setCustomVariable", 2, "Groups", group, "visit"]);
        pageTrackerLogger("Setting custom dimension for group = " + group);
        _paq.push(["setCustomDimension", 1, group]);
      }

      // Instead of ignoring internal users set a flag
      const _isInternalUser =
        isAdmin() ||
        isInternalUser() ||
        isInIgnoreUserList() ||
        hasInternalToken(profile);
      pageTrackerLogger(
        "Will Matomo consider you an internal user? " + _isInternalUser
      );
      _paq.push([
        "setCustomVariable",
        3,
        "Internal User",
        _isInternalUser,
        "visit",
      ]);
      _paq.push(["setCustomDimension", 2, _isInternalUser]);

      _paq.push(["trackPageView"]);
      _paq.push(["enableLinkTracking"]);
      _paq.push(["trackAllContentImpressions"]);
      _paq.push(["enableHeartBeatTimer"]);
    },
  },
};
</script>

<style scoped>
.bg,
.hyp-container {
  background: #191a1a;
  color: #fff;
}
</style>
